import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BookOpen, Trash2 } from 'lucide-react'
import ConfirmModal from '../ui/ConfirmModal'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'

function StudentCourseList() {
  const location = useLocation()
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const student_id = new URLSearchParams(location.search).get('student_id')
  const [unassignModalOpen, setUnassignModalOpen] = useState(false)
  const [courseToUnassign, setCourseToUnassign] = useState(null)
  const api = apiService()

  const getCourses = async () => {
    if (!student_id) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      // GET courses/student/:id includes both direct and batch-assigned courses
      const response = await api.get(`courses/student/${student_id}`)
      if (response.status === 200 && response.data.courses) {
        const assignedCourses = response.data.courses.filter((c) => c.assigned)
        const courses = assignedCourses.map((c) => ({
          _id: c._id,
          title: c.title,
          description: c.description || '',
          thumbnail: c.thumbnail || '',
          status: c.status || 'active',
          assignment_id: c.assignment_id || null,
          assignedViaBatch: c.assignedViaBatch || false,
        }))
        setData(courses)
      }
    } catch (error) {
      console.error('Error fetching courses:', error)
      toast.error(error.response?.data?.message || 'Failed to fetch courses')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getCourses()
  }, [student_id])

  const handleUnassignClick = (course) => {
    setCourseToUnassign(course)
    setUnassignModalOpen(true)
  }

  const handleConfirmUnassign = async () => {
    if (courseToUnassign && student_id) {
      try {
        const response = await api.del(`courses/${courseToUnassign._id}/student/${student_id}`)
        if (response.status === 200) {
          toast.success('Course unassigned successfully')
          await getCourses()
          setUnassignModalOpen(false)
          setCourseToUnassign(null)
        }
      } catch (error) {
        console.error('Error unassigning course:', error)
        toast.error(error.response?.data?.message || 'Failed to unassign course')
      }
    }
  }

  return (
    <div className="bg-white border rounded-[10px] border-[#F0F0F0]">
      <div className="flex justify-between items-center px-5 pt-5">
        <div className="text-[#030303] text-lg leading-[21.6px] border-0 font-semibold">
          Course Assignments
        </div>
        <Link
          to="/students"
          className="bg-[#DE3C7D] text-white px-4 py-2 rounded-md hover:bg-[#c72c6a] transition-colors"
        >
          Back to Students
        </Link>
      </div>
      <div className="overflow-x-auto mt-2">
        <table className="w-full">
          <thead className="bg-neutral-50">
            <tr>
              <th className="text-left border text-[#535862] text-sm leading-[16.8px] w-[70px] px-5 py-3.5 border-[#F0F0F0]">
                S.No
              </th>
              <th className="text-left border text-[#535862] text-sm leading-[16.8px] w-[240px] px-5 py-3.5 border-[#F0F0F0]">
                Course Name
              </th>
              <th className="text-left border text-[#535862] text-sm leading-[16.8px] w-[400px] px-5 py-3.5 border-[#F0F0F0]">
                Description
              </th>
              <th className="text-left border text-[#535862] text-sm leading-[16.8px] w-[120px] px-5 py-3.5 border-[#F0F0F0]">
                Status
              </th>
              <th className="text-center border text-[#535862] text-sm leading-[16.8px] w-[120px] px-2 py-3.5 border-l border-[#F0F0F0]">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <tr key={idx}>
                  {Array.from({ length: 5 }).map((_, colIdx) => (
                    <td
                      key={colIdx}
                      className={
                        colIdx === 4
                          ? 'text-center border px-2 py-2 border-l border-[#F0F0F0]'
                          : 'border text-[#535862] text-sm leading-[16.8px] px-5 py-2 border-[#F0F0F0]'
                      }
                    >
                      <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length > 0 ? (
              data.map((item, index) => (
                <tr key={item._id} className="border border-[#F0F0F0]">
                  <td className="border text-[#030303] text-sm leading-[16.8px] px-5 py-2 border-[#F0F0F0]">
                    {String(index + 1).padStart(2, '0')}
                  </td>
                  <td className="border text-[#535862] text-sm leading-[16.8px] px-5 py-2 border-[#F0F0F0]">
                    <div className="flex items-center gap-2">
                      {item.thumbnail ? (
                        <img
                          src={
                            item.thumbnail.startsWith('/uploads/')
                              ? `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${item.thumbnail}`
                              : item.thumbnail
                          }
                          alt={item.title}
                          className="w-8 h-8 object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-[#DE3C7D] to-[#c8356f] rounded flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <span className="font-medium">{item.title || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="border text-[#535862] text-sm leading-[16.8px] px-5 py-2 border-[#F0F0F0]">
                    <div className="max-w-[400px] truncate" title={item.description}>
                      {item.description || 'No description'}
                    </div>
                  </td>
                  <td className="border px-5 py-2 border-[#F0F0F0]">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        item.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {item.status === 'active' ? 'Active' : 'Draft'}
                    </span>
                  </td>
                  <td className="text-center border px-2 py-2 border-l border-[#F0F0F0]">
                    <div className="flex items-center justify-center gap-2.5">
                      <button
                        className="flex items-center gap-1 text-red-600 hover:text-red-700 transition-colors"
                        onClick={() => handleUnassignClick(item)}
                        title="Unassign course"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="text-xs">Unassign</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="5"
                  align="center"
                  className="py-8 text-[#535862] text-sm"
                >
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No courses assigned to this student</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {unassignModalOpen && (
        <ConfirmModal
          open={unassignModalOpen}
          onCancel={() => {
            setUnassignModalOpen(false)
            setCourseToUnassign(null)
          }}
          onConfirm={handleConfirmUnassign}
          title="Unassign Course"
          message={
            courseToUnassign
              ? `Are you sure you want to unassign "${courseToUnassign.title}" from this student?`
              : 'Are you sure you want to unassign this course?'
          }
        />
      )}
    </div>
  )
}

export default StudentCourseList

